import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Globe, LogOut, Moon, Sun } from 'lucide-react';
import { api, fijarAccessToken } from '../lib/api.ts';
import { cambiarIdioma, idiomaGuardado } from '../i18n/index.ts';
import { alternarTema, temaActual } from '../lib/tema.ts';
import { useCerrarSesion, useSesion } from '../lib/sesion.ts';
import { Avatar, Boton, BotonCirculo } from './ui.tsx';

interface Pestana {
  a: string;
  etiqueta: string;
  search?: Record<string, string>;
}

/**
 * Top nav "lumin": tarjeta blanca con logo a la izquierda, pestañas centradas (la
 * activa en píldora negra) y controles circulares a la derecha. Sin barra lateral.
 */
export function Encabezado() {
  const { t } = useTranslation();
  const { data: sesion } = useSesion();
  const cerrarSesion = useCerrarSesion();
  const [tema, setTema] = useState(temaActual());
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const impersonando = document.cookie.split('; ').includes('pm_impersonando=1');

  async function volverAPlataforma() {
    try {
      const r = await api<{ accessToken?: string }>('/auth/volver', { metodo: 'POST' });
      fijarAccessToken(r.accessToken ?? null);
    } finally {
      window.location.href = '/super';
    }
  }

  const nombreVisible = sesion?.branding.nombreVisible ?? t('app.nombre');
  const rol = sesion?.usuario.rol;
  const pestanas: Pestana[] = !sesion
    ? []
    : sesion.cliente
      ? [
          { a: '/', etiqueta: t('nav.inicio') },
          { a: '/productos', etiqueta: t('productos.titulo'), search: { tipoContenido: 'PROMOCION' } },
          { a: '/galeria', etiqueta: t('nav.galeria') },
          { a: '/cuenta', etiqueta: t('nav.cuenta') },
        ]
      : rol === 'SUPERADMIN'
        ? [] // el superadmin tiene una sola vista: sin tabs
        : [
            { a: '/', etiqueta: t('nav.inicio') },
            { a: '/admin/marcas', etiqueta: 'Marcas' },
            { a: '/admin/productos', etiqueta: 'Productos' },
            { a: '/admin/clientes', etiqueta: 'Clientes' },
            { a: '/admin/plantillas', etiqueta: 'Plantillas' },
            { a: '/admin/campanias', etiqueta: 'Campañas' },
            { a: '/admin/analitica', etiqueta: 'Analítica' },
          ];

  const activa = (a: string) => (a === '/' ? ruta === '/' : ruta === a || ruta.startsWith(`${a}/`));

  return (
    <header className="sticky top-0 z-40 px-4 pt-4 md:px-8">
      <div className="tarjeta flex items-center gap-3 px-4 py-2.5 md:px-5">
        {/* Marca */}
        <Link to="/" className="flex flex-none items-center gap-2.5">
          {sesion?.branding.logoUrl ? (
            <img src={sesion.branding.logoUrl} alt={nombreVisible} className="h-8 object-contain" />
          ) : (
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full"
              style={{ backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }}
            >
              <span className="h-3 w-3 rounded-full bg-current" />
            </span>
          )}
          <span className="text-[17px] font-medium tracking-tight">{nombreVisible}</span>
        </Link>

        {/* Pestañas centradas */}
        <nav className="sin-barra mx-auto flex min-w-0 items-center gap-1 overflow-x-auto px-2">
          {pestanas.map((p) => (
            <Link
              key={p.a}
              to={p.a}
              search={p.search}
              className="flex-none rounded-full px-4 py-2 text-[13px] font-medium transition"
              style={
                activa(p.a)
                  ? { backgroundColor: 'var(--boton-bg)', color: 'var(--boton-texto)' }
                  : { color: 'var(--texto-suave)' }
              }
            >
              {p.etiqueta}
            </Link>
          ))}
        </nav>

        {/* Botón estrella del cliente: siempre visible, no se lo traga el scroll del nav */}
        {sesion?.cliente ? (
          <Link
            to="/marca"
            className="flex-none rounded-full px-4 py-2.5 text-[13px] font-semibold shadow-[var(--sombra)] transition hover:-translate-y-0.5"
            style={{ backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }}
          >
            + {t('nav.nuevoPost')}
          </Link>
        ) : null}

        {/* Controles */}
        <div className="flex flex-none items-center gap-2">
          {impersonando && rol !== 'SUPERADMIN' ? (
            <Boton variante="acento" tamano="sm" onClick={() => void volverAPlataforma()}>
              <ArrowLeft size={14} /> {t('nav.volver')}
            </Boton>
          ) : null}
          <BotonCirculo contorno aria-label={t('nav.tema')} onClick={() => setTema(alternarTema())}>
            {tema === 'oscuro' ? <Sun size={17} /> : <Moon size={17} />}
          </BotonCirculo>
          <BotonCirculo
            contorno
            aria-label={t('nav.idioma')}
            title={idiomaGuardado() === 'pt' ? 'Português → Español' : 'Español → Português'}
            onClick={() => cambiarIdioma(idiomaGuardado() === 'pt' ? 'es' : 'pt')}
          >
            <Globe size={17} />
          </BotonCirculo>
          {sesion ? (
            <>
              <BotonCirculo contorno aria-label={t('nav.salir')} title={t('nav.salir')} onClick={() => void cerrarSesion()}>
                <LogOut size={17} />
              </BotonCirculo>
              <Avatar
                nombre={sesion.cliente?.nombreNegocio ?? sesion.usuario.username}
                className="h-11 w-11"
              />
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
